/**
 * DEMO DATA. Every client, spot and pot here is invented for the prototype and has nothing
 * to do with Bella Fiori's real clients. Dates are generated relative to "today" so the
 * verdicts stay meaningful whenever the demo is opened.
 *
 * Built to show every state: greens, a client requirement (white only), a spot requirement (large only),
 * an exclusion (no red), 12-month reds, a pot the records place elsewhere, a retired pot,
 * and hand-ons (a pot collected early in the route that fits a later spot).
 */
import type { EventEnvelope } from "@hitl/core";
import {
  addMonths,
  addDays,
  calendarDate,
  buildHistory,
  createAssessor,
  createCatalog,
  DEFAULT_REPEAT_POLICY,
  suggestPlan,
  type CalendarDate,
  type Client,
  type Pot,
  type Slot,
} from "@hitl/rotations-domain";
import type { DaySetup } from "../state/session";
import { emptyPlanner, type PlannerState } from "../state/plannerState";
import type { SettingsState } from "../state/settingsState";

const pot = (id: string, colour: string[], size: "large" | "medium" | "small", active = true): Pot => ({
  id,
  attributes: { colour, size: [size] },
  active,
});

const POTS: Pot[] = [
  pot("A1", ["white"], "large"),
  pot("A2", ["white", "green"], "large"),
  pot("A3", ["pink"], "large"),
  pot("A4", ["white"], "large"),
  pot("A5", ["yellow"], "large"),
  pot("A6", ["red"], "large"),
  pot("B1", ["white"], "medium"),
  pot("B2", ["red"], "medium"),
  pot("B3", ["purple"], "medium"),
  pot("B4", ["white"], "medium"),
  pot("B5", ["orange"], "medium"),
  pot("B6", ["pink", "white"], "medium"),
  pot("C1", ["white"], "small"),
  pot("C2", ["yellow"], "small"),
  pot("C3", ["red"], "small"),
  pot("C4", ["blue"], "small"),
  pot("C5", ["green"], "small", false), // retired
  pot("C6", ["white"], "small"),
  pot("D1", ["white"], "large"),
  pot("D2", ["green"], "medium"),
  pot("D3", ["white", "pink"], "large"),
  pot("D4", ["yellow"], "small"),
  pot("D5", ["white"], "medium"),
  pot("D6", ["purple"], "large"),
  pot("D7", ["orange"], "medium"),
  pot("D8", ["white"], "small"),
];

const CLIENTS: Client[] = [
  { id: "spar", name: "Brandwag Spar", requirements: [{ attribute: "colour", rule: "only", values: ["white"], note: "Only white" }] },
  { id: "smit", name: "Smit Prokureurs", requirements: [] },
  { id: "dental", name: "Waterkloof Dental", requirements: [{ attribute: "colour", rule: "exclude", values: ["red"] }] },
  { id: "guest", name: "Oranje Guesthouse", requirements: [] },
  { id: "physio", name: "Park Physio", requirements: [] },
  { id: "gold", name: "Goldfields Pharmacy", requirements: [] },
  { id: "wmed", name: "Welkom Medical", requirements: [{ attribute: "colour", rule: "exclude", values: ["red", "orange"] }] },
];

const SLOTS: Slot[] = [
  { id: "spar-entrance", clientId: "spar", label: "Entrance", requirements: [] },
  { id: "smit-reception", clientId: "smit", label: "Reception", requirements: [{ attribute: "size", rule: "only", values: ["large"] }] },
  { id: "smit-boardroom", clientId: "smit", label: "Boardroom", requirements: [] },
  { id: "dental-waiting", clientId: "dental", label: "Waiting room", requirements: [] },
  { id: "dental-counter", clientId: "dental", label: "Front counter", requirements: [] },
  { id: "guest-lobby", clientId: "guest", label: "Lobby", requirements: [] },
  { id: "guest-breakfast", clientId: "guest", label: "Breakfast room", requirements: [] },
  { id: "physio-reception", clientId: "physio", label: "Reception", requirements: [] },
  { id: "gold-front", clientId: "gold", label: "Front shop", requirements: [] },
  { id: "gold-back", clientId: "gold", label: "Dispensary", requirements: [] },
  { id: "wmed-reception", clientId: "wmed", label: "Reception", requirements: [] },
];

let seq = 0;
function seed(type: string, payload: Record<string, unknown>): EventEnvelope {
  seq += 1;
  return {
    id: `demo-${seq}`,
    type,
    schemaVersion: 1,
    occurredAt: `${String(payload.on)}T09:00:00+02:00`,
    recordedBy: "demo",
    deviceId: "demo-import",
    deviceSeq: seq,
    payload,
  };
}

const placed = (potId: string, slotId: string, on: CalendarDate) =>
  seed("pot.placed", { potId, clientId: SLOTS.find((s) => s.id === slotId)!.clientId, slotId, on });
const collected = (potId: string, slotId: string, on: CalendarDate) =>
  seed("pot.collected", { potId, clientId: SLOTS.find((s) => s.id === slotId)!.clientId, slotId, on });

/**
 * DEMO asset register: costs by size; the A–C pots were bought last tax year, the D pots this
 * one. C6, D5 and D8 deliberately have no cost, to show the "missing cost" alert.
 */
const DEMO_COST: Record<string, number> = { large: 52000, medium: 38000, small: 26000 };
const NO_COST = new Set(["C6", "D5", "D8"]);
function withDemoAsset(p: Pot): Pot {
  if (NO_COST.has(p.id)) return p;
  const size = p.attributes.size?.[0] ?? "medium";
  return {
    ...p,
    asset: {
      boughtOn: calendarDate(p.id.startsWith("D") ? "2026-04-08" : "2025-05-20"),
      costCents: DEMO_COST[size] ?? 38000,
      supplier: "Decor Direct (demo)",
    },
  };
}

export interface DemoTenant {
  readonly tenantName: string;
  readonly setup: DaySetup;
  /** Her saved routes and a starting schedule (today + two more days this rotation week). */
  readonly planner: PlannerState;
  /** Starting settings: the editable catalog, rules and people. */
  readonly settings: SettingsState;
  /** History imported before go-live (never synced from this phone). */
  readonly seedEvents: readonly EventEnvelope[];
}

export function createDemoTenant(today: CalendarDate): DemoTenant {
  seq = 0;
  const ago = (months: number, days = 0) => addDays(addMonths(today, -months), -days);

  // Older placements that make some pots red at some clients today.
  const older: EventEnvelope[] = [
    placed("A2", "smit-reception", ago(7)), collected("A2", "smit-reception", ago(6)),
    placed("B4", "spar-entrance", ago(5)), collected("B4", "spar-entrance", ago(4)),
    placed("A4", "guest-lobby", ago(9)), collected("A4", "guest-lobby", ago(8)),
    placed("C6", "dental-counter", ago(3)), collected("C6", "dental-counter", ago(2)),
    placed("B3", "physio-reception", ago(14)), collected("B3", "physio-reception", ago(13)), // > 12 months: fine again
  ];

  // What stands at each spot this morning (placed about a month ago).
  const standing: EventEnvelope[] = [
    placed("A1", "spar-entrance", ago(1)),
    placed("A3", "smit-reception", ago(1)),
    placed("B1", "smit-boardroom", ago(1)),
    placed("B5", "dental-waiting", ago(1)),
    placed("C2", "dental-counter", ago(1)),
    placed("A5", "guest-lobby", ago(1)),
    placed("B6", "guest-breakfast", ago(1)),
    placed("C4", "physio-reception", ago(1)),
    placed("D2", "gold-front", ago(1)),
    placed("D4", "gold-back", ago(1)),
    placed("D6", "wmed-reception", ago(1)),
    // Recorded at a client that isn't on today's route: scanning it shows "records say it's elsewhere".
    placed("B2", "smit-boardroom", ago(20)), collected("B2", "smit-boardroom", ago(19)),
  ];

  // One earlier write-off, so the asset register and month views have a real example.
  const writtenOff: EventEnvelope[] = [seed("pot.written-off", { potId: "D7", on: ago(2), reason: "coating", note: "Enamel flaking on the rim" })];

  const seedEvents = [...older, ...standing, ...writtenOff];
  const catalog = createCatalog(POTS.map(withDemoAsset), CLIENTS, SLOTS);
  const route = {
    on: today,
    slotIds: ["spar-entrance", "smit-reception", "smit-boardroom", "dental-waiting", "dental-counter", "guest-lobby", "guest-breakfast", "physio-reception"],
  };
  const historyKnownSince = ago(18);
  const history = buildHistory(seedEvents);
  const assessor = createAssessor({ catalog, history, policy: DEFAULT_REPEAT_POLICY, historyKnownSince });
  const plan = suggestPlan(route, { catalog, history, assessor }, { spares: 2 });

  const routes = [
    { id: "bloem-noord", name: "Bloem Noord", slotIds: ["spar-entrance", "smit-reception", "smit-boardroom", "dental-waiting", "dental-counter"] },
    { id: "bloem-sentraal", name: "Bloem Sentraal", slotIds: ["guest-lobby", "guest-breakfast", "physio-reception"] },
    { id: "welkom", name: "Welkom run", slotIds: ["gold-front", "gold-back", "wmed-reception"] },
  ];
  const planner: PlannerState = {
    ...emptyPlanner,
    routes,
    schedule: { [today]: "bloem-noord", [addDays(today, 1)]: "bloem-sentraal", [addDays(today, 2)]: "welkom" },
  };

  return {
    tenantName: "Demo Florals",
    setup: { catalog, route, plan, policy: DEFAULT_REPEAT_POLICY, historyKnownSince },
    planner,
    settings: {
      business: "Demo Florals",
      catalog: { pots: POTS.map(withDemoAsset), clients: CLIENTS, slots: SLOTS },
      rules: { windowMonths: DEFAULT_REPEAT_POLICY.windowMonths, historyKnownSince, defaultSpares: 2 },
      people: { names: ["Arlene", "Jess"], current: "Arlene" },
    },
    seedEvents,
  };
}
